import { ethers } from 'ethers';
import { isSaaSyBypassed } from './saasyBypass';

export const LEGACY_SAASY_HASH_ONLY_SOURCE = 'legacy_saasy_hash_only' as const;
export const OVERPASS_OFFCHAIN_V1_SOURCE = 'overpass_offchain_v1' as const;
export const OVERPASS_ONCHAIN_V1_SOURCE = 'overpass_onchain_v1' as const;
export const OVERPASS_OFFCHAIN_VERSION = '1' as const;

export type AgreementSource =
  | typeof LEGACY_SAASY_HASH_ONLY_SOURCE
  | typeof OVERPASS_OFFCHAIN_V1_SOURCE
  | typeof OVERPASS_ONCHAIN_V1_SOURCE;

export type AgreementValidationStatus = 'VALIDATED' | 'INVALID' | 'PENDING';

export interface AgreementCapabilities {
  canSubmit: boolean;
  canApproveInDb: boolean;
  canValidate: boolean;
  canGeneratePdf: boolean;
  canCheckout: boolean;
  canCounterSign: boolean;
  canExecutePayment: boolean;
  readOnly: boolean;
}

export interface BidAgreementSummary {
  id?: number | null;
  source?: string | null;
  version?: string | null;
  Countersignature?: {
    id?: number | null;
    validation_status?: string | null;
  } | null;
}

export interface BidWithAgreementSummary {
  BidAgreement?: BidAgreementSummary | null;
}

export interface AgreementNftInput {
  contractAddress: string;
  tokenId: string;
  collectionName?: string | null;
}

export interface AgreementDealInput {
  id: number;
  title?: string | null;
  paymentCurrency?: string | null;
  brandWallet: string;
  minBudget?: string | number | null;
  maxBudget?: string | number | null;
  royaltyPercentage?: string | number | null;
  biddingExpiration?: string | null;
  licenseExpiration?: string | null;
}

export interface AgreementBidInput {
  amount: string;
  percentageAmount?: string | number | null;
  expiration?: string | null;
  nonce: string;
}

export interface BuildAgreementSnapshotInput {
  deal: AgreementDealInput;
  bid: AgreementBidInput;
  nft: AgreementNftInput;
  holderWallet: string;
  renderedTerms: string;
  sourceTerms?: unknown;
  dynamicData?: unknown;
  createdAt?: string;
}

export interface OverpassAgreementSnapshot {
  source: typeof OVERPASS_OFFCHAIN_V1_SOURCE;
  version: typeof OVERPASS_OFFCHAIN_VERSION;
  createdAt: string;
  holderWallet: string;
  brandWallet: string;
  deal: {
    id: number;
    title: string | null;
    paymentCurrency: string | null;
    minBudget: string | null;
    maxBudget: string | null;
    royaltyPercentage: string | null;
    biddingExpiration: string | null;
    licenseExpiration: string | null;
  };
  bid: {
    amount: string;
    percentageAmount: string | null;
    expiration: string | null;
    nonce: string;
  };
  nft: {
    contractAddress: string;
    tokenId: string;
    collectionName: string | null;
  };
  renderedTerms: string;
  sourceTerms: unknown;
  dynamicData: unknown;
}

export interface OverpassAgreementTypedData {
  domain: ethers.TypedDataDomain;
  types: Record<string, Array<{ name: string; type: string }>>;
  primaryType: 'Agreement';
  message: {
    agreementHash: string;
    holderWallet: string;
    brandWallet: string;
    nftContractAddress: string;
    tokenId: string;
    dealId: string;
    bidAmount: string;
    royaltyPercentage: string;
    nonce: string;
    expiresAt: string;
  };
}

export interface OverpassAgreementCountersignatureTypedData {
  domain: ethers.TypedDataDomain;
  types: Record<string, Array<{ name: string; type: string }>>;
  primaryType: 'AgreementCountersignature';
  message: {
    agreementHash: string;
    bidAgreementId: string;
    holderWallet: string;
    brandWallet: string;
    counterSignerWallet: string;
    dealId: string;
    counterDynamicDataHash: string;
    nonce: string;
    signedAt: string;
  };
}

const OFFCHAIN_CAPABILITIES: AgreementCapabilities = {
  canSubmit: true,
  canApproveInDb: true,
  canValidate: true,
  canGeneratePdf: true,
  canCheckout: false,
  canCounterSign: true,
  canExecutePayment: false,
  readOnly: false,
};

const SAASY_CAPABILITIES: AgreementCapabilities = {
  canSubmit: true,
  canApproveInDb: true,
  canValidate: true,
  canGeneratePdf: true,
  canCheckout: true,
  canCounterSign: true,
  canExecutePayment: true,
  readOnly: false,
};

const READ_ONLY_CAPABILITIES: AgreementCapabilities = {
  canSubmit: false,
  canApproveInDb: false,
  canValidate: false,
  canGeneratePdf: false,
  canCheckout: false,
  canCounterSign: false,
  canExecutePayment: false,
  readOnly: true,
};

// Legacy SaaSy bids on a deal with Overpass legacy execution enabled: the
// brand can select them in the DB; execution runs through the legacy
// execution checkout, not SaaSy.
const LEGACY_EXECUTION_CAPABILITIES: AgreementCapabilities = {
  ...READ_ONLY_CAPABILITIES,
  canApproveInDb: true,
  readOnly: false,
};

const RESERVED_CAPABILITIES: AgreementCapabilities = {
  ...READ_ONLY_CAPABILITIES,
  readOnly: false,
};

export function getAgreementSourceForBid(
  bid: BidWithAgreementSummary | null | undefined
): AgreementSource {
  const source = bid?.BidAgreement?.source;

  if (source === OVERPASS_OFFCHAIN_V1_SOURCE) {
    return OVERPASS_OFFCHAIN_V1_SOURCE;
  }

  if (source === OVERPASS_ONCHAIN_V1_SOURCE) {
    return OVERPASS_ONCHAIN_V1_SOURCE;
  }

  return LEGACY_SAASY_HASH_ONLY_SOURCE;
}

export function getAgreementCapabilities(
  source: AgreementSource | BidWithAgreementSummary | null | undefined,
  options: { bypassSaaSy?: boolean; legacyExecutionEnabled?: boolean } = {}
): AgreementCapabilities {
  const resolvedSource =
    typeof source === 'string' ? source : getAgreementSourceForBid(source);
  const bypassSaaSy = options.bypassSaaSy ?? isSaaSyBypassed();

  if (resolvedSource === OVERPASS_OFFCHAIN_V1_SOURCE) {
    return { ...OFFCHAIN_CAPABILITIES };
  }

  if (resolvedSource === OVERPASS_ONCHAIN_V1_SOURCE) {
    return { ...RESERVED_CAPABILITIES };
  }

  if (!bypassSaaSy) {
    return { ...SAASY_CAPABILITIES };
  }

  return options.legacyExecutionEnabled
    ? { ...LEGACY_EXECUTION_CAPABILITIES }
    : { ...READ_ONLY_CAPABILITIES };
}

export function normalizeAddress(address: string): string {
  return ethers.utils.getAddress(address);
}

export function stableJson(value: unknown): string {
  return JSON.stringify(sortForStableJson(value));
}

export function getCanonicalAgreementHash(snapshot: unknown): string {
  return ethers.utils.keccak256(ethers.utils.toUtf8Bytes(stableJson(snapshot)));
}

export function buildOverpassAgreementSnapshot(
  input: BuildAgreementSnapshotInput
): OverpassAgreementSnapshot {
  return {
    source: OVERPASS_OFFCHAIN_V1_SOURCE,
    version: OVERPASS_OFFCHAIN_VERSION,
    createdAt: input.createdAt ?? new Date().toISOString(),
    holderWallet: normalizeAddress(input.holderWallet),
    brandWallet: normalizeAddress(input.deal.brandWallet),
    deal: {
      id: input.deal.id,
      title: input.deal.title ?? null,
      paymentCurrency: input.deal.paymentCurrency ?? null,
      minBudget: nullableString(input.deal.minBudget),
      maxBudget: nullableString(input.deal.maxBudget),
      royaltyPercentage: nullableString(input.deal.royaltyPercentage),
      biddingExpiration: input.deal.biddingExpiration ?? null,
      licenseExpiration: input.deal.licenseExpiration ?? null,
    },
    bid: {
      amount: input.bid.amount,
      percentageAmount: nullableString(input.bid.percentageAmount),
      expiration: input.bid.expiration ?? null,
      nonce: input.bid.nonce,
    },
    nft: {
      contractAddress: normalizeAddress(input.nft.contractAddress),
      tokenId: input.nft.tokenId,
      collectionName: input.nft.collectionName ?? null,
    },
    renderedTerms: input.renderedTerms,
    sourceTerms: input.sourceTerms ?? null,
    dynamicData: input.dynamicData ?? null,
  };
}

export function buildOverpassAgreementTypedData({
  snapshot,
  agreementHash,
  chainId,
}: {
  snapshot: OverpassAgreementSnapshot;
  agreementHash: string;
  chainId: number;
}): OverpassAgreementTypedData {
  return {
    domain: {
      name: 'Overpass IP Agreement',
      version: OVERPASS_OFFCHAIN_VERSION,
      chainId,
    },
    types: {
      Agreement: [
        { name: 'agreementHash', type: 'bytes32' },
        { name: 'holderWallet', type: 'address' },
        { name: 'brandWallet', type: 'address' },
        { name: 'nftContractAddress', type: 'address' },
        { name: 'tokenId', type: 'string' },
        { name: 'dealId', type: 'string' },
        { name: 'bidAmount', type: 'string' },
        { name: 'royaltyPercentage', type: 'string' },
        { name: 'nonce', type: 'string' },
        { name: 'expiresAt', type: 'string' },
      ],
    },
    primaryType: 'Agreement',
    message: {
      agreementHash,
      holderWallet: snapshot.holderWallet,
      brandWallet: snapshot.brandWallet,
      nftContractAddress: snapshot.nft.contractAddress,
      tokenId: snapshot.nft.tokenId,
      dealId: String(snapshot.deal.id),
      bidAmount: snapshot.bid.amount,
      royaltyPercentage: snapshot.deal.royaltyPercentage ?? '',
      nonce: snapshot.bid.nonce,
      expiresAt:
        snapshot.bid.expiration ?? snapshot.deal.biddingExpiration ?? '',
    },
  };
}

export function buildOverpassCountersignatureDynamicData({
  brandWallet,
  companySignatureDate,
}: {
  brandWallet: string;
  companySignatureDate: string;
}): Record<string, string> {
  return {
    COMPANY_ADDRESS: normalizeAddress(brandWallet),
    COMPANY_SIGNATURE_DATE: companySignatureDate,
  };
}

export function buildOverpassAgreementCountersignatureTypedData({
  agreementHash,
  bidAgreementId,
  holderWallet,
  brandWallet,
  counterSignerWallet,
  dealId,
  counterDynamicDataHash,
  nonce,
  signedAt,
  chainId,
}: {
  agreementHash: string;
  bidAgreementId: number | string;
  holderWallet: string;
  brandWallet: string;
  counterSignerWallet: string;
  dealId: number | string;
  counterDynamicDataHash: string;
  nonce: string;
  signedAt: string;
  chainId: number;
}): OverpassAgreementCountersignatureTypedData {
  return {
    domain: {
      name: 'Overpass IP Agreement Countersignature',
      version: OVERPASS_OFFCHAIN_VERSION,
      chainId,
    },
    types: {
      AgreementCountersignature: [
        { name: 'agreementHash', type: 'bytes32' },
        { name: 'bidAgreementId', type: 'string' },
        { name: 'holderWallet', type: 'address' },
        { name: 'brandWallet', type: 'address' },
        { name: 'counterSignerWallet', type: 'address' },
        { name: 'dealId', type: 'string' },
        { name: 'counterDynamicDataHash', type: 'bytes32' },
        { name: 'nonce', type: 'string' },
        { name: 'signedAt', type: 'string' },
      ],
    },
    primaryType: 'AgreementCountersignature',
    message: {
      agreementHash,
      bidAgreementId: String(bidAgreementId),
      holderWallet: normalizeAddress(holderWallet),
      brandWallet: normalizeAddress(brandWallet),
      counterSignerWallet: normalizeAddress(counterSignerWallet),
      dealId: String(dealId),
      counterDynamicDataHash,
      nonce,
      signedAt,
    },
  };
}

export function recoverOverpassAgreementSigner(
  typedData:
    | OverpassAgreementTypedData
    | OverpassAgreementCountersignatureTypedData,
  signature: string
): string {
  return normalizeAddress(
    ethers.utils.verifyTypedData(
      typedData.domain,
      typedData.types,
      typedData.message,
      signature
    )
  );
}

export function verifyOverpassAgreementSignature({
  typedData,
  signature,
  expectedSigner,
}: {
  typedData: OverpassAgreementTypedData;
  signature: string;
  expectedSigner: string;
}): boolean {
  return (
    recoverOverpassAgreementSigner(typedData, signature) ===
    normalizeAddress(expectedSigner)
  );
}

export function buildFinalAgreementDynamicData({
  proposalDynamicData,
  counterDynamicData,
  holderSignature,
  counterSignature,
}: {
  proposalDynamicData: unknown;
  counterDynamicData: Record<string, string>;
  holderSignature: string;
  counterSignature: string;
}): Record<string, unknown> {
  return {
    ...(isPlainObject(proposalDynamicData) ? proposalDynamicData : {}),
    ...counterDynamicData,
    ONCHAIN_OFFERER_SIGNATURE: holderSignature,
    ONCHAIN_PROMISOR_SIGNATURE: counterSignature,
  };
}

export function verifyOverpassAgreementCountersignature({
  typedData,
  signature,
  expectedSigner,
}: {
  typedData: OverpassAgreementCountersignatureTypedData;
  signature: string;
  expectedSigner: string;
}): boolean {
  return (
    recoverOverpassAgreementSigner(typedData, signature) ===
    normalizeAddress(expectedSigner)
  );
}

function sortForStableJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortForStableJson);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((result, key) => {
      result[key] = sortForStableJson((value as Record<string, unknown>)[key]);
      return result;
    }, {});
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

function nullableString(
  value: string | number | null | undefined
): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  return String(value);
}
